import { useEffect, useMemo } from "react";

/**
 * Gallery theming primitive — loads the chosen Google Font pair on demand,
 * derives CSS variables from accent color + font preset, and returns a style
 * object the orchestrator can spread onto its root element.
 *
 * We intentionally avoid Tailwind's `theme.extend` here: customization happens
 * per-gallery at render time, not at compile time. CSS vars keep the cost
 * negligible and let inner components reach for them via Tailwind's arbitrary
 * value syntax (`text-[var(--gallery-accent)]`) without bringing motion props
 * into low-level grids.
 */

const FONT_PRESETS = {
  editorial: {
    family: "Cormorant Garamond",
    weights: "wght@300;400;500;600;700",
    serif: "'Cormorant Garamond', 'Times New Roman', serif",
    sans: "'Inter', system-ui, sans-serif",
    label: "Editorial",
  },
  modern: {
    family: "Cabinet Grotesk",
    // Cabinet Grotesk is hosted on Fontshare, not Google Fonts. We fall back to
    // a stylistic Google substitute (Manrope) so the public gallery still
    // renders premium type without requiring an extra <link>.
    googleFamily: "Manrope",
    weights: "wght@400;500;600;700;800",
    serif: "'Manrope', 'Cabinet Grotesk', sans-serif",
    sans: "'Inter', system-ui, sans-serif",
    label: "Modern",
  },
  quiet: {
    family: "Work Sans",
    weights: "wght@300;400;500;600",
    serif: "'Work Sans', system-ui, sans-serif",
    sans: "'Inter', system-ui, sans-serif",
    label: "Quiet",
  },
};

function googleFontHref(preset) {
  const family = (preset.googleFamily || preset.family).replace(/\s/g, "+");
  return `https://fonts.googleapis.com/css2?family=${family}:${preset.weights}&family=Inter:wght@300;400;500;600;700&display=swap`;
}

function hexToRgb(hex) {
  if (!hex || typeof hex !== "string") return "201 169 97"; // default gold
  const h = hex.replace("#", "");
  const v = h.length === 3 ? h.split("").map((c) => c + c).join("") : h.padEnd(6, "0");
  const r = parseInt(v.slice(0, 2), 16) || 0;
  const g = parseInt(v.slice(2, 4), 16) || 0;
  const b = parseInt(v.slice(4, 6), 16) || 0;
  return `${r} ${g} ${b}`;
}

export default function useGalleryTheme(gallery) {
  const presetKey = gallery?.font_preset || "editorial";
  const preset = FONT_PRESETS[presetKey] || FONT_PRESETS.editorial;
  const accent = gallery?.accent_color || "#C9A961";
  const accentRgb = hexToRgb(accent);
  const overlayOpacity = gallery?.cover_overlay_opacity ?? 0.3;
  const overlayTone = gallery?.cover_overlay_tone === "light" ? "255 255 255" : "0 0 0";

  // Load the Google Font pair exactly once per preset. Cleanup is deliberately
  // skipped: if the user switches preset we want both sheets cached so the
  // transition is instant; the browser dedupes by href so re-loading is free.
  useEffect(() => {
    const href = googleFontHref(preset);
    if (document.querySelector(`link[data-gallery-font="${presetKey}"]`)) return;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = href;
    link.setAttribute("data-gallery-font", presetKey);
    document.head.appendChild(link);
  }, [presetKey, preset]);

  const style = useMemo(
    () => ({
      "--gallery-accent": accent,
      "--gallery-accent-rgb": accentRgb,
      "--gallery-font-serif": preset.serif,
      "--gallery-font-sans": preset.sans,
      "--gallery-overlay-opacity": overlayOpacity,
      "--gallery-overlay-rgb": overlayTone,
      // Scope the design-system gold vars to the accent so PremiumCover's
      // foil rules, tickers and hairlines follow the per-gallery accent.
      // Default accent equals the canonical gold, so untouched galleries
      // don't shift.
      "--gold-base": accent,
      "--gold-foil": accent,
      "--gold-deep": accent,
      // Cover text ink follows the overlay tone: a LIGHT (white) wash needs
      // dark type or the title becomes invisible over bright photos.
      "--gallery-cover-ink": gallery?.cover_overlay_tone === "light" ? "#1C1917" : "#FAF9F6",
    }),
    [accent, accentRgb, preset.serif, preset.sans, overlayOpacity, overlayTone, gallery?.cover_overlay_tone],
  );

  return { style, preset, accent };
}
